import { ChevronLeft, ChevronRight } from "lucide-react";
import { MOCK_CALENDAR, WEEKDAY_LABELS } from "@/src/common/components/app-mock/mock-community";
import { MockCard } from "@/src/common/components/app-mock/primitives";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

interface DayDotsProps {
	hasEvent: boolean;
	hasMeeting: boolean;
	eventColor: string;
	meetingColor: string;
}

function DayDots({ hasEvent, hasMeeting, eventColor, meetingColor }: DayDotsProps) {
	return (
		<span className="absolute bottom-0.5 left-1/2 flex -translate-x-1/2 gap-0.5">
			{hasEvent ? <span className="size-1 rounded-full" style={{ background: eventColor }} /> : null}
			{hasMeeting ? <span className="size-1 rounded-full" style={{ background: meetingColor }} /> : null}
		</span>
	);
}

interface CalendarWidgetProps {
	legend?: boolean;
	selectedDay?: number;
	onSelectDay?: (day: number) => void;
	className?: string;
	eventDotColor?: string;
	meetingDotColor?: string;
}

export function CalendarWidget({ legend, selectedDay, onSelectDay, className = "", eventDotColor, meetingDotColor }: CalendarWidgetProps) {
	const { palette } = useCommunityVariant();
	const cells: Array<number | null> = [];
	for (let i = 0; i < MOCK_CALENDAR.firstWeekday; i += 1) cells.push(null);
	for (let day = 1; day <= MOCK_CALENDAR.daysInMonth; day += 1) cells.push(day);
	const selected = selectedDay ?? MOCK_CALENDAR.today;
	const eventColor = eventDotColor ?? "#171717";
	const meetingColor = meetingDotColor ?? palette.accent;

	return (
		<MockCard className={className} dataMock="calendar-widget">
			<div className="flex items-center justify-between px-1">
				<span className="text-[11px] font-semibold text-neutral-900">{MOCK_CALENDAR.label}</span>
				<span className="flex items-center gap-1 text-neutral-500" aria-hidden="true">
					<ChevronLeft className="size-3.5" />
					<ChevronRight className="size-3.5" />
				</span>
			</div>
			<div className="mt-2 grid grid-cols-7 text-center text-[9px] font-medium text-neutral-400" aria-hidden="true">
				{WEEKDAY_LABELS.map((label) => (
					<span key={label}>{label}</span>
				))}
			</div>
			<div className="mt-1 grid grid-cols-7 gap-y-0.5 text-center text-[10px]">
				{cells.map((day, index) => {
					if (day === null) return <span key={`empty-${index.toString()}`} aria-hidden="true" />;
					const isSelected = day === selected;
					const isToday = day === MOCK_CALENDAR.today;
					const hasMeeting = MOCK_CALENDAR.meetingDays.includes(day);
					const hasEvent = MOCK_CALENDAR.eventDays.includes(day);
					const classes = `relative mx-auto flex size-6 items-center justify-center rounded-full font-medium ${
						isSelected ? "text-white" : isToday ? "font-bold" : "text-neutral-700"
					}`;
					const style = isSelected ? { background: palette.accent } : isToday ? { color: palette.accent } : undefined;
					const dotProps = {
						hasEvent,
						hasMeeting,
						eventColor: isSelected ? "#fff" : eventColor,
						meetingColor: isSelected ? "#fff" : meetingColor,
					};
					return onSelectDay ? (
						<button key={day} type="button" onClick={() => onSelectDay(day)} aria-pressed={isSelected} className={classes} style={style}>
							{day}
							<DayDots {...dotProps} />
						</button>
					) : (
						<span key={day} className={classes} style={style} aria-hidden="true">
							{day}
							<DayDots {...dotProps} />
						</span>
					);
				})}
			</div>
			{legend ? (
				<div className="mt-2 flex items-center gap-3 px-1 text-[10px] text-neutral-500" aria-hidden="true">
					<span className="inline-flex items-center gap-1">
						<span className="size-1.5 rounded-full" style={{ background: eventColor }} />
						Events
					</span>
					<span className="inline-flex items-center gap-1">
						<span className="size-1.5 rounded-full" style={{ background: meetingColor }} />
						Meetings
					</span>
				</div>
			) : null}
		</MockCard>
	);
}
